import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { UniLookupComponent } from '../../../shared/components/uni-lookup/uni-lookup.component';
import { UniListComponent } from '../../../shared/components/uni-list/uni-list.component';
import { UniAddComponent } from '../../../shared/components/uni-add/uni-add.component';
import { ResidenceLookupComponent } from '../../../shared/components/residence-lookup/residence-lookup.component';
import { ResidenceListComponent } from '../../../shared/components/residence-list/residence-list.component';
import { ResidenceAddComponent } from '../../../shared/components/residence-add/residence-add.component';
import { IStrapiDocument, UpdateDetails } from '../../../interfaces/strapi';
import StrapiService from '../../../services/strapi.service';
import { University } from '../../../shared/models/university.model';
import { Residence } from '../../../shared/models/residence.model';
import { ApiSearchResponse } from '../../../interfaces/api';
import { ResidenceService } from '../../../services/residence.service';
import { UniService } from '../../../services/uni.service';
import { UserService } from '../../../services/user.service';
import { User } from '../../../shared/models/user.model';

@Component({
    selector: 'app-update-uni-accommodation-unihomes',
    templateUrl: './update-uni-accommodation-unihomes.component.html',
    styleUrls: ['./update-uni-accommodation-unihomes.component.scss'],
    imports: [
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        ModalComponent,
        UniLookupComponent,
        UniListComponent,
        UniAddComponent,
        ResidenceLookupComponent,
        ResidenceListComponent,
    ],
})
export class UpdateUniAccommodationUnihomesComponent implements OnInit {
  // uni lookup modal stuff
  @ViewChild('uniLookupModal') public readonly uniLookupModal!: ModalComponent;

  public showUniLookupModal = signal(false);

  @ViewChild('uniLookup') public readonly uniLookup!: UniLookupComponent;

  public uniLookupSearchTerm = signal('');

  // uni list modal stuff
  @ViewChild('uniListModal') public readonly uniListModal!: ModalComponent;

  public showUniListModal = signal(false);

  @ViewChild('uniList') public readonly uniList!: UniListComponent;

  // uni add modal stuff
  @ViewChild('uniAddModal') public readonly uniAddModal!: ModalComponent;

  public showUniAddModal = signal(false);

  @ViewChild('uniAdd') public readonly uniAdd!: UniAddComponent;

  public addUniPrefill = signal('');

  @ViewChild('residenceLookupModal')
  public readonly residenceLookupModal!: ModalComponent;

  public showResidenceLookupModal = signal(false);

  @ViewChild('residenceLookup')
  public readonly residenceLookup!: ResidenceLookupComponent;

  public residenceLookupSearchTerm = signal('');

  // residence list modal stuff
  @ViewChild('residenceListModal')
  public readonly residenceListModal!: ModalComponent;

  public showResidenceListModal = signal(false);

  @ViewChild('residenceList')
  public readonly residenceList!: ResidenceListComponent;

  // residence add modal stuff
  @ViewChild('residenceAddModal')
  public readonly residenceAddModal!: ModalComponent;

  @ViewChild('residenceAdd')
  public readonly residenceAdd!: ResidenceAddComponent;

  public addResidencePrefill = signal('');

  public userForm!: UntypedFormGroup;

  public strapiFromData: UpdateDetails | undefined;

  private selectedUniversity!: University;

  private selectedResidence!: Residence;

  public doingChanges = signal(false);

  public shouldShowCYC = signal(false);

  public showTooltipModal = signal(false);

  public tooltipModalContent = signal('');

  public accommodationStayingText = signal<string | null>('');

  public isPathwayProvider = signal(false);

  public submited = signal(false);

  public updateError = signal(false);

  public updateErrorMessage = signal('');

  @Output() private readonly updateComplete: EventEmitter<boolean> =
    new EventEmitter();

  constructor(
    private readonly authService: AuthService,
    private readonly formBuilder: UntypedFormBuilder,
    public readonly strapi: StrapiService,
    private readonly residenceService: ResidenceService,
    private readonly uniService: UniService,
    private readonly user: UserService
  ) {}

  ngOnInit(): void {
    if (this.authService.currentUser) {
      this.selectedResidence = this.authService.currentUser.accommodation;
    }
    this.strapi
      .getUpdateDetails()
      .subscribe((data: IStrapiDocument<UpdateDetails>) => {
        this.strapiFromData = data.data.attributes;
      });

    this.initializeForm();
  }

  public initializeForm(): void {
    const uniId = '';
    const uniName = '';
    const accommodationId =
      this.authService.currentUser?.accommodation.uuid ?? '';
    const accommodationName =
      this.authService.currentUser?.accommodation.name ?? '';

    this.userForm = this.formBuilder.group({
      university_id: [uniId, { validators: [Validators.required.bind(this)] }],
      university_name: [uniName],
      residence_id: [
        accommodationId,
        { validators: [Validators.required.bind(this)] },
      ],
      residence_name: [accommodationName],
    });

    this.uniLookupSearchTerm.set('');
    this.residenceLookupSearchTerm.set(
      String(this.authService.currentUser?.accommodation.name)
    );
  }

  public get userControl(): {
    [key: string]: AbstractControl;
  } {
    return this.userForm.controls;
  }

  public changeShowAddUniModal(lookupTerm: string): void {
    this.hideUniSelectionModals();
    if (lookupTerm) {
      this.addUniPrefill.set(lookupTerm);
    }
    this.showUniAddModal.set(true);
  }

  public residenceSelected(residence: Residence): void {
    this.hideResidenceSelectionModals();
    this.userForm.get('residence_id')!.setValue(residence.uuid);
    this.userForm.get('residence_name')!.setValue(residence.name);
    this.residenceLookupSearchTerm.set(residence.name);
    this.addResidencePrefill.set(residence.name);
    this.selectedResidence = residence;
  }

  public hideUniSelectionModals(): void {
    this.showUniLookupModal.set(false);
    this.showUniListModal.set(false);
  }

  private setAccommodationFieldText(uni: University): void {
    this.isPathwayProvider.set(uni.is_pathway_provider);
    if (uni.is_pathway_provider) {
      this.accommodationStayingText.set(
        this.strapiFromData!.staying_pathway_provider
      );
      return;
    }
    this.accommodationStayingText.set(this.strapiFromData!.staying);
  }

  public universitySelected(university: University): void {
    this.hideUniSelectionModals();
    this.userForm.get('university_id')?.setValue(university.uuid);
    this.userForm.get('university_name')?.setValue(university.name);
    this.uniLookupSearchTerm.set(university.name);
    this.addUniPrefill.set(university.name);
    this.selectedUniversity = university;
    this.setAccommodationFieldText(university);
  }

  public residenceEntered(residence: Residence): void {
    this.userForm.get('residence_id')?.setValue(residence.uuid);
    this.userForm.get('residence_name')?.setValue(residence.name);
    this.residenceLookupSearchTerm.set(residence.name);
    this.selectedResidence = residence;
  }

  public hideResidenceSelectionModals(): void {
    this.showResidenceLookupModal.set(false);
    this.showResidenceListModal.set(false);
  }

  public handleAlternateResidence(slug: string): void {
    this.residenceService.getResidenceBySlug(slug).subscribe({
      next: (residence) => {
        const theResidenceResponse = <ApiSearchResponse>residence;
        const res = <Residence>theResidenceResponse.data;
        this.userForm.get('residence_id')!.setValue(res.uuid);
        this.userForm.get('residence_name')!.setValue(res.name);
        this.selectedResidence = res;
        this.hideResidenceSelectionModals();
      },
    });
  }

  public displayUniLookupModal(): void {
    this.showUniLookupModal.set(true);
    this.uniLookup.giveFocus();
  }

  public displayUniListModal($event: Event): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.showUniListModal.set(true);
  }

  public handleNotAtUni(): void {
    this.uniService.getImNotAtUniversityObject().subscribe({
      next: (university) => {
        const theUniResponse = <ApiSearchResponse>university;
        const uni = <University>theUniResponse.data;
        this.userForm.get('university_id')!.setValue(uni.uuid);
        this.userForm.get('university_name')!.setValue(uni.name);
        this.selectedUniversity = uni;
        this.accommodationStayingText.set(this.strapiFromData!.staying);
        this.hideUniSelectionModals();
        this.setAccommodationFieldText(uni);
      },
    });
  }

  public openTooltip(id: string) {
    this.showTooltipModal.set(true);
    this.showTooltipModal.set(true);

    type PageDataIndex = keyof typeof this.strapiFromData;

    const currentRoute = id as PageDataIndex;
    if (this.strapiFromData)
      this.tooltipModalContent.set(
        this.strapiFromData[currentRoute] as string
      );
  }

  public displayResidenceLookupModal(): void {
    this.showResidenceLookupModal.set(true);
    this.residenceLookup.giveFocus();
  }

  public displayResidenceListModal($event: Event): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.showResidenceListModal.set(true);
  }

  public universityEntered(university: University): void {
    this.userForm.get('university_id')?.setValue(university.uuid);
    this.userForm.get('university_name')?.setValue(university.name);
    this.uniLookupSearchTerm.set(university.name);
    this.showUniAddModal.set(false);
    this.selectedUniversity = university;
    this.accommodationStayingText.set(this.strapiFromData!.staying);
  }

  submitUserForm($e: any) {
    $e.preventDefault();

    this.submited.set(true);

    if (!this.userForm.valid) return;

    const clone = User.fromJson(this.authService.currentUser!);
    clone.universityId = this.selectedUniversity.uuid;
    clone.accommodationId = this.selectedResidence.uuid;
    clone.postcode = '';

    this.user.updateUser(clone).subscribe({
      next: () => {
        this.updateComplete.emit(true);
      },
      error: () => {
        this.updateError.set(true);
      },
    });
  }

  public cancel() {
    this.updateComplete.emit(false);
  }
}
